/**
 * Service để gửi dữ liệu chấm công đến Google Apps Script
 * Version với CORS support (dùng Google Apps Script Web App với CORS enabled)
 */

// Trim URL để tránh khoảng trắng
const GOOGLE_SCRIPT_URL = (process.env.REACT_APP_GOOGLE_SCRIPT_URL || '').trim();

/**
 * Convert File/Blob sang Base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove data:image/jpeg;base64, prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Convert File/Blob sang Base64 với retry mechanism
 */
const fileToBase64WithRetry = async (file, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fileToBase64(file);
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        // Exponential backoff: 100ms, 200ms, 400ms
        await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, i)));
      }
    }
  }
  throw lastError;
};

/**
 * Gửi dữ liệu chấm công đến Google Apps Script
 * Sử dụng form-urlencoded để tránh CORS preflight request
 */
export const submitAttendance = async (data) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình. Vui lòng set REACT_APP_GOOGLE_SCRIPT_URL trong .env');
  }

  try {
    // Convert image to base64 nếu có (với retry mechanism)
    let imageBase64 = null;
    if (data.image) {
      imageBase64 = await fileToBase64WithRetry(data.image);
    }
    
    // API Key (optional - để backward compatible)
    const API_KEY = process.env.REACT_APP_API_KEY || '';

    // Sử dụng URLSearchParams để tránh preflight CORS
    // Google Apps Script Web App xử lý form-urlencoded tốt hơn JSON
    const params = new URLSearchParams();
    params.append('action', 'attendance');
    params.append('employee_id', data.employee_id || '');
    params.append('employee_code', data.employee_code || '');
    params.append('employee_name', data.employee_name || '');
    params.append('type', data.type || 'checkin'); // 'checkin' hoặc 'checkout'
    params.append('latitude', data.latitude || '');
    params.append('longitude', data.longitude || '');
    params.append('accuracy', data.accuracy || '');
    params.append('address', data.address || '');
    if (data.explanation) {
      params.append('explanation', data.explanation);
    }
    // VIP: cho phép set giờ chấm công (epoch millis)
    if (data.timestamp_ms !== undefined && data.timestamp_ms !== null && data.timestamp_ms !== '') {
      params.append('timestamp_ms', String(data.timestamp_ms));
    }
    if (data.project_code) {
      params.append('project_code', data.project_code);
    }
    if (data.project_name) {
      params.append('project_name', data.project_name);
    }
    // VIP flag (để backend luôn coi là hợp lệ, kể cả dùng GPS thực tế)
    if (data.vip !== undefined && data.vip !== null && data.vip !== '') {
      params.append('vip', String(data.vip));
    }
    if (imageBase64) {
      params.append('image_base64', imageBase64);
    }
    // API Key (optional - để backward compatible)
    if (API_KEY) {
      params.append('api_key', API_KEY);
    }

    // Gửi POST request với form-urlencoded để tránh CORS preflight
    // Note: Google Apps Script Web App cần được deploy với "Execute as: Me" và "Who has access: Anyone"
    const response = await fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
    }

    const contentType = response.headers.get('content-type') || '';
    let result;
    
    if (contentType.includes('application/json')) {
      result = await response.json();
    } else {
      // Nếu không phải JSON, thử parse text
      const text = await response.text();
      console.log('Response text:', text);
      try {
        result = JSON.parse(text);
      } catch (e) {
        throw new Error('Server trả về dữ liệu không hợp lệ: ' + text.substring(0, 200));
      }
    }

    return result;

  } catch (error) {
    console.error('Error submitting attendance:', error);
    throw error;
  }
};

/**
 * Test kết nối đến Google Apps Script
 */
export const testGoogleScript = async () => {
  if (!GOOGLE_SCRIPT_URL) {
    return { ok: false, error: 'Google Script URL chưa được cấu hình' };
  }

  try {
    const response = await fetch(GOOGLE_SCRIPT_URL);
    const data = await response.json();
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

/**
 * Lấy danh sách nhân viên (với caching)
 */
export const getEmployees = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  // Check cache first
  if (!forceRefresh) {
    const { getCached, setCached, CACHE_TTL } = await import('../utils/cache');
    const cached = getCached('employees', CACHE_TTL.EMPLOYEES);
    if (cached) {
      console.log('[Cache] Using cached employees');
      return cached;
    }
  }

  try {
    const response = await fetch(`${GOOGLE_SCRIPT_URL}?action=employees`);
    const result = await response.json();
    if (result.ok && result.data) {
      // Cache the result
      const { setCached, CACHE_TTL } = await import('../utils/cache');
      setCached('employees', result.data);
      return result.data;
    } else {
      throw new Error(result.error || 'Không thể tải danh sách nhân viên');
    }
  } catch (error) {
    console.error('Error loading employees:', error);
    throw error;
  }
};

/**
 * Lấy danh sách địa điểm dự án hoạt động (để kiểm tra vị trí) - với caching
 */
export const getProjectLocations = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  // Check cache first
  if (!forceRefresh) {
    const { getCached, setCached, CACHE_TTL } = await import('../utils/cache');
    const cached = getCached('projectLocations', CACHE_TTL.PROJECT_LOCATIONS);
    if (cached) {
      console.log('[Cache] Using cached project locations');
      return cached;
    }
  }

  try {
    // Thêm cache-buster để tránh bị cache (đặc biệt trên Safari/iOS/Cloudflare)
    const url = `${GOOGLE_SCRIPT_URL}?action=projectLocations&t=${Date.now()}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (result.ok && result.data) {
      // Cache the result
      const { setCached, CACHE_TTL } = await import('../utils/cache');
      setCached('projectLocations', result.data);
      return result.data;
    } else {
      // Nếu không có dữ liệu, trả về mảng rỗng thay vì throw error
      console.warn('No project locations found or error:', result.error);
      return [];
    }
  } catch (error) {
    console.error('Error loading project locations:', error);
    // Trả về mảng rỗng thay vì throw error để app vẫn hoạt động
    return [];
  }
};

/**
 * Lấy chấm công hôm nay của nhân viên (dùng employee_code làm khóa chính) - với caching
 */
export const getTodayAttendance = async (employeeCode, forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!employeeCode) {
    throw new Error('Employee code is required');
  }

  // Check cache first
  if (!forceRefresh) {
    const { getCached, setCached, CACHE_TTL } = await import('../utils/cache');
    const cacheKey = `todayAttendance_${employeeCode}`;
    const cached = getCached(cacheKey, CACHE_TTL.TODAY_ATTENDANCE);
    if (cached !== null) { // null is valid (no attendance yet)
      console.log('[Cache] Using cached today attendance');
      return cached;
    }
  }

  try {
    // Thêm cache-buster để tránh bị cache (đặc biệt trên Android WebView/Chrome + Cloudflare)
    const url = `${GOOGLE_SCRIPT_URL}?action=todayAttendance&employee_code=${encodeURIComponent(employeeCode)}&t=${Date.now()}`;
    console.log('Fetching today attendance from:', url);

    const response = await fetch(url, { cache: 'no-store' });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Today attendance response:', result);
    
    if (result.ok) {
      const data = result.data; // Có thể là null nếu chưa chấm công
      // Cache the result
      const { setCached, CACHE_TTL } = await import('../utils/cache');
      const cacheKey = `todayAttendance_${employeeCode}`;
      setCached(cacheKey, data);
      return data;
    } else {
      throw new Error(result.error || 'Không thể tải chấm công hôm nay');
    }
  } catch (error) {
    console.error('Error loading today attendance:', error);
    // Trả về null thay vì throw error để app vẫn hoạt động
    return null;
  }
};

/**
 * Lấy lịch sử chấm công của nhân viên (dùng employee_code làm khóa chính)
 */
export const getAttendanceHistory = async (employeeCode, startDate = null, endDate = null) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!employeeCode) {
    throw new Error('Employee code is required');
  }

  try {
    // Thêm cache-buster để tránh bị cache (đặc biệt trên Android WebView/Chrome + Cloudflare)
    let url = `${GOOGLE_SCRIPT_URL}?action=attendance&employee_code=${encodeURIComponent(employeeCode)}&t=${Date.now()}`;
    if (startDate) {
      url += `&start_date=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      url += `&end_date=${encodeURIComponent(endDate)}`;
    }

    const response = await fetch(url, { cache: 'no-store' });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (result.ok) {
      // Trả về mảng rỗng nếu không có data thay vì throw error
      return result.data || [];
    } else {
      console.error('API returned error:', result.error);
      // Trả về mảng rỗng thay vì throw error để app vẫn hoạt động
      return [];
    }
  } catch (error) {
    console.error('Error loading attendance history:', error);
    // Trả về mảng rỗng thay vì throw error để app vẫn hoạt động
    return [];
  }
};
